import React, {useState} from "react";
import {Box, Tooltip, Typography, IconButton, Popover} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {alpha} from "@mui/material/styles";
import {SEV, NEUTRAL, TABULAR, TYPE} from "./tokens";
import {
    fmtMoneyShort, fmtDeltaShort, deltaColor, fmtNum, EMPTY, severityOf, statusMeta, isNilValue,
    fmtYd, ydColor, ydHeadline, decompParts, DECOMP_BASIS, fmtSignedMoney, fmtDay, providerCoversNetwork, shortProvider,
} from "./format";

/** `site · partner [· network]` — the network only when the partner name does not already carry it. */
export const Identity = ({site, provider, network, prefix, sx}) => {
    const showNetwork = network && !providerCoversNetwork(provider, network, prefix);
    const text = [site || EMPTY, shortProvider(provider), showNetwork ? network : null].filter(Boolean).join(" · ");
    return (
        <Typography component="span" noWrap title={text} sx={{fontSize: 12.5, minWidth: 0, ...sx}}>
            <Box component="span" sx={{color: "text.primary", fontWeight: 500}}>{site || EMPTY}</Box>
            <Box component="span" sx={{color: "text.disabled"}}> · </Box>
            <Box component="span" sx={{color: "text.secondary"}}>{shortProvider(provider)}</Box>
            {showNetwork && (
                <>
                    <Box component="span" sx={{color: "text.disabled"}}> · </Box>
                    <Box component="span" sx={{color: "text.disabled"}}>{network}</Box>
                </>
            )}
        </Typography>
    );
};

/** 8 px dot; nothing for OK / idle. */
export const SeverityDot = ({status, size = 8, sx}) => {
    const sev = severityOf(status);
    if (sev === "ok" || sev === "idle") return null;
    return (
        <Box component="span" aria-hidden="true" sx={{
            display: "inline-block", width: size, height: size, borderRadius: "50%", flex: "0 0 auto",
            bgcolor: (t) => SEV[sev].dot(t), ...sx,
        }}/>
    );
};

/** Outlined grey dot for OK rows (drawer header only). */
export const OkDot = ({size = 8}) => (
    <Box component="span" aria-hidden="true" sx={{
        display: "inline-block", width: size, height: size, borderRadius: "50%", flex: "0 0 auto",
        border: "1.5px solid", borderColor: "grey.400", boxSizing: "border-box",
    }}/>
);

/** Status chip: filled for error/warning, outlined for info/idle/ok. Renders as a span so it is safe inside any parent. */
export const StatusChip = ({status, short = false, sx}) => {
    const sev = severityOf(status);
    const meta = statusMeta(status);
    const S = SEV[sev];
    const text = short ? meta.short : meta.label;
    if (!text) return null;
    return (
        <Box component="span" sx={{
            display: "inline-flex", alignItems: "center", height: 18, px: 0.75, borderRadius: "4px", whiteSpace: "nowrap",
            ...TYPE.chip, lineHeight: 1,
            bgcolor: (t) => S.chipBg(t), color: (t) => S.chipText(t),
            border: S.chipBorder ? `1px solid ${S.chipBorder}` : "1px solid transparent",
            ...sx,
        }}>
            {text}
        </Box>
    );
};

/** Revenue with a right-anchored share bar behind the number. */
export const ShareBarCell = ({value, max, muted = false}) => {
    const w = max > 0 && value > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
    return (
        <Box sx={{position: "relative", width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "flex-end"}}>
            <Box aria-hidden="true" sx={{
                position: "absolute", right: 0, top: 6, bottom: 6, width: `${w}%`, borderRadius: "2px",
                bgcolor: (t) => alpha(t.palette.primary.main, muted ? 0.06 : 0.14),
            }}/>
            <Typography component="span" sx={{position: "relative", fontWeight: 600, fontSize: 13, ...TABULAR, pr: 0.5}}>
                {fmtMoneyShort(value)}
            </Typography>
        </Box>
    );
};

/** Last-day revenue: disabled + tooltip when partial, red bold when $0 with money at risk. `tip` = yesterday tooltip body. */
export const LastCell = ({value, partial, risk, tip}) => {
    const zeroRisk = (Number(value) || 0) === 0 && risk > 5;
    const el = (
        <Typography component="span" sx={{
            fontSize: 13, ...TABULAR,
            color: partial ? "text.disabled" : zeroRisk ? "error.main" : "text.primary",
            fontWeight: zeroRisk && !partial ? 600 : 400,
        }}>
            {fmtMoneyShort(value)}
        </Typography>
    );
    if (partial) return <Tooltip title="Not yet reported by the partner" placement="top" arrow>{el}</Tooltip>;
    if (tip) return <Tooltip title={tip} placement="top" arrow enterDelay={400}>{el}</Tooltip>;
    return el;
};

/**
 * Tooltip body shared by Δd, Last and the phone rows:
 * line 1 `Sep 13 $70 vs Sep 12 $82 · −15%`, line 2 the API reason, line 3 `Why vs day before: RPM −$8.7 · coverage +$3.8`
 * (the $ decomposition explains the move vs the day before — never the 7-day delta; largest driver first, bold; omitted
 * when every part is $0).
 */
export const YdTip = ({y, base}) => {
    if (!y) return null;
    if (y.state === "pending") return <span>not yet reported by the partner{y.day ? ` for ${fmtDay(y.day)}` : ""}</span>;
    const parts = y.decomp_significant === false ? [] : decompParts(y);
    return (
        <Box component="span" sx={{display: "block", ...TABULAR, fontSize: 11.5, lineHeight: 1.35}}>
            <Box component="span" sx={{display: "block", fontWeight: 600}}>{ydHeadline(y, base)}</Box>
            {y.reason && <Box component="span" sx={{display: "block", opacity: 0.85}}>{y.reason}</Box>}
            {parts.length > 0 && (
                <Box component="span" sx={{display: "block", opacity: 0.85}}>
                    {DECOMP_BASIS}:{" "}
                    {parts.map((p, i) => (
                        <React.Fragment key={p.key}>
                            {i > 0 && " · "}
                            <Box component="span" sx={{fontWeight: i === 0 ? 600 : 400}}>{p.label} {fmtSignedMoney(p.value)}</Box>
                        </React.Fragment>
                    ))}
                </Box>
            )}
        </Box>
    );
};

/** Δd cell: coloured only when the tag moved; "◌ —" while pending; "new" without history; grey otherwise. */
export const YdCell = ({y, base, sx}) => {
    const el = (
        <Typography component="span" sx={{
            fontSize: 13, fontWeight: y && y.state === "moved" ? 600 : 400, ...TABULAR, color: ydColor(y, base), whiteSpace: "nowrap", ...sx,
        }}>
            {fmtYd(y, base)}
        </Typography>
    );
    if (!y) return el;
    return <Tooltip title={<YdTip y={y} base={base}/>} placement="top" arrow enterDelay={300}>{el}</Tooltip>;
};

/**
 * Δ revenue vs previous window. Text only, never icons.
 * On alarm rows (`severity` error / warning) it stays neutral so the row keeps one coloured signal (the status), like IdxCell.
 */
export const DeltaCell = ({value, prev, cur, severity, sx}) => {
    const alarm = severity === "error" || severity === "warning";
    return (
        <Typography component="span" sx={{
            fontSize: 13, fontWeight: alarm ? 400 : 600, ...TABULAR, color: alarm ? "text.secondary" : deltaColor(value, prev, cur),
            ...sx,
        }}>
            {fmtDeltaShort(value, prev, cur)}
        </Typography>
    );
};

/**
 * Index vs group median: ×1.5 green when ≥ 1.25, red when ≤ 0.6.
 * On alarm rows (`severity` error / warning) it stays neutral so the row keeps one coloured signal (the status).
 */
export const IdxCell = ({value, severity}) => {
    if (isNilValue(value)) return <Typography component="span" sx={{fontSize: 13, color: "text.disabled"}}>{EMPTY}</Typography>;
    const n = Number(value);
    const alarm = severity === "error" || severity === "warning";
    const color = alarm ? "text.secondary" : n >= 1.25 ? "success.dark" : n <= 0.6 ? "error.main" : "text.secondary";
    return (
        <Typography component="span" sx={{fontSize: 13, ...TABULAR, color, fontWeight: !alarm && (n >= 1.25 || n <= 0.6) ? 600 : 400}}>
            ×{fmtNum(n, 1)}
        </Typography>
    );
};

export const idleTextSx = {color: NEUTRAL.idleText};

/**
 * `?` help (brief §8): a 20 px IconButton at the right of a panel header → Popover with caption-size lines (≤ 6).
 * `lines` = array of strings; `label` names the panel for the aria-label.
 */
export const HelpPopover = ({label, lines, sx}) => {
    const [anchor, setAnchor] = useState(null);
    const open = Boolean(anchor);
    return (
        <>
            <IconButton size="small" aria-label={`About ${label}`} aria-haspopup="dialog" aria-expanded={open}
                        onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}
                        sx={{width: 20, height: 20, p: 0, color: "text.disabled", "&:hover": {color: "text.secondary"}, ...sx}}>
                <HelpOutlineIcon sx={{fontSize: 16}}/>
            </IconButton>
            <Popover open={open} anchorEl={anchor} onClose={() => setAnchor(null)} onClick={(e) => e.stopPropagation()}
                     anchorOrigin={{vertical: "bottom", horizontal: "right"}} transformOrigin={{vertical: "top", horizontal: "right"}}
                     PaperProps={{sx: {p: 1.5, maxWidth: 360, mt: 0.5}}}>
                <Typography component="div" sx={{...TYPE.eyebrow, mb: 0.5}}>{label}</Typography>
                {lines.map((l, i) => (
                    <Typography key={i} component="div" sx={{...TYPE.caption, fontSize: 12, lineHeight: 1.45, color: "text.primary"}}>{l}</Typography>
                ))}
            </Popover>
        </>
    );
};
